import React, { useContext, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import NRConfirmationModal from './components/NRConfirmationModal/NRConfirmationModal';
import { UserContext } from './App';

const idleTimeoutSeconds = process.env.REACT_APP_AUTH0_NAMESPACE + 'idle_seconds';
const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000;

const IdleLogoutHandler = () => {
  const { logout, isAuthenticated } = useAuth0();
  const { idToken } = useContext(UserContext);

  // Compute timeouts once
  const [totalTimeout] = useState(idToken[idleTimeoutSeconds] ? idToken[idleTimeoutSeconds] * 1000 : DEFAULT_TIMEOUT_MS);
  const [warningTime] = useState(totalTimeout <= 120000 ? totalTimeout / 2 : 2 * 60 * 1000);

  // Modal & countdown state
  const [showModal, setShowModal] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Prevent duplicate logout
  const hasLoggedOutRef = useRef(false);

  // Keep modal flag fresh
  const showModalRef = useRef(showModal);
  useEffect(() => {
    showModalRef.current = showModal;
  }, [showModal]);

  // Clear all timers & state
  const clearAll = () => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    hasLoggedOutRef.current = false;
    setShowModal(false);
    setCountdown(0);
  };

  // Schedule warning & logout
  const scheduleTimers = () => {
    warningTimerRef.current = window.setTimeout(() => {
      setShowModal(true);
      let sec = Math.floor(warningTime / 1000);
      setCountdown(sec);

      countdownIntervalRef.current = window.setInterval(() => {
        sec -= 1;
        setCountdown(sec);
        if (sec <= 0 && countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      }, 1000);
    }, totalTimeout - warningTime);

    logoutTimerRef.current = window.setTimeout(() => {
      if (!hasLoggedOutRef.current) {
        hasLoggedOutRef.current = true;
        logout({ returnTo: window.location.origin });
      }
    }, totalTimeout);
  };

  // Reset on user activity (unless modal is open)
  const resetTimers = () => {
    if (!isAuthenticated || showModalRef.current) return;
    clearAll();
    scheduleTimers();
  };

  // Start/cleanup when auth state or timeout changes
  useEffect(() => {
    if (!isAuthenticated) return;

    clearAll();
    scheduleTimers();

    const events = ['mousemove', 'mousedown', 'click', 'keydown', 'scroll', 'touchstart'];
    events.forEach(evt => document.addEventListener(evt, resetTimers));

    return () => {
      clearAll();
      events.forEach(evt => document.removeEventListener(evt, resetTimers));
    };
  }, [isAuthenticated, totalTimeout]);

  // “Stay” and “Logout” handlers
  const handleStay = () => {
    clearAll();
    scheduleTimers();
  };
  const handleLogout = () => {
    if (!hasLoggedOutRef.current) {
      hasLoggedOutRef.current = true;
      clearAll();
      logout({ returnTo: window.location.origin });
    }
  };

  // Compute minutes & seconds display
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  // 1) Build an array of JSX fragments:
  const timeParts = [];

  if (minutes > 0) {
    timeParts.push(
      <React.Fragment key="m">
        <strong>{minutes}</strong> minute{minutes !== 1 ? 's' : ''}
      </React.Fragment>
    );
  }

  if (seconds > 0 || minutes === 0) {
    timeParts.push(
      <React.Fragment key="s">
        <strong>{seconds}</strong> second{seconds !== 1 ? 's' : ''}
      </React.Fragment>
    );
  }

  // 2) Interleave with “ ” if you have both parts:
  let timeDisplay;
  if (timeParts.length === 2) {
    timeDisplay = (
      <>
        {timeParts[0]} {timeParts[1]}
      </>
    );
  } else {
    timeDisplay = timeParts[0] || null;
  }

  return (
    <>
      {showModal && (
        <NRConfirmationModal
          title={<Typography variant="h3">Session Will Expire Soon</Typography>}
          content={
            <Box display="flex" flexDirection="column" gap={3}>
              <Typography variant="body1">You have been idle and will be automatically logged out due to security reasons in:</Typography>
              <Typography variant="body1">{timeDisplay}</Typography>
              <Typography variant="body1">Would you like to keep working?</Typography>
            </Box>
          }
          open
          cancelLabel="Log Out"
          actionLabel="Keep Working"
          handleClose={handleStay}
          handleCancel={handleLogout}
          handleActionConfirm={handleStay}
        />
      )}
    </>
  );
};

export default IdleLogoutHandler;
